import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import LocationPage from "./pages/LocationPage";
import ServicePage from "./pages/ServicePage";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import ServicesOverview from "./pages/ServicesOverview";
const PricingPage = lazy(() => import("./pages/PricingPage"));
const PricingCalculatorPage = lazy(() => import("./pages/PricingCalculatorPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const JoinTeamPage = lazy(() => import("./pages/JoinTeamPage"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
import ServiceAreaPage from "./pages/ServiceAreaPage";
import LASubRegionPage from "./pages/LASubRegionPage";
import RegionBlogPage from "./pages/RegionBlogPage";
import RegionFAQPage from "./pages/RegionFAQPage";
import { LocationProvider } from "./lib/locations";


import SacramentoSpanishPage from "./pages/SacramentoSpanishPage";
import LASpanishPage from "./pages/LASpanishPage";
import RosevilleSpanishPage from "./pages/RosevilleSpanishPage";
import PasadenaSpanishPage from "./pages/PasadenaSpanishPage";
import SanJoseSpanishPage from "./pages/SanJoseSpanishPage";
import SanDiegoSpanishPage from "./pages/SanDiegoSpanishPage";
import SanFranciscoSpanishPage from "./pages/SanFranciscoSpanishPage";
import IrvineSpanishPage from "./pages/IrvineSpanishPage";
import OaklandSpanishPage from "./pages/OaklandSpanishPage";
import WestLASpanishPage from "./pages/WestLASpanishPage";
import SantaClaraSpanishPage from "./pages/SantaClaraSpanishPage";
import MarinSpanishPage from "./pages/MarinSpanishPage";
import ContraCostaSpanishPage from "./pages/ContraCostaSpanishPage";
import SolanoSpanishPage from "./pages/SolanoSpanishPage";
import AlamedaSpanishPage from "./pages/AlamedaSpanishPage";
import FremontSpanishPage from "./pages/FremontSpanishPage";
import HaywardSpanishPage from "./pages/HaywardSpanishPage";
import BerkeleySpanishPage from "./pages/BerkeleySpanishPage";
import PleasantonSpanishPage from "./pages/PleasantonSpanishPage";
import DublinSpanishPage from "./pages/DublinSpanishPage";
import LivermoreSpanishPage from "./pages/LivermoreSpanishPage";
import TriValleySpanishPage from "./pages/TriValleySpanishPage";
import SanRamonSpanishPage from "./pages/SanRamonSpanishPage";
import OrangeCountySpanishPage from "./pages/OrangeCountySpanishPage";
import HuntingtonBeachSpanishPage from "./pages/HuntingtonBeachSpanishPage";
import NewportBeachSpanishPage from "./pages/NewportBeachSpanishPage";
import VallejoSpanishPage from "./pages/VallejoSpanishPage";
import FairfieldSpanishPage from "./pages/FairfieldSpanishPage";
import VacavilleSpanishPage from "./pages/VacavilleSpanishPage";
import BeniciaSpanishPage from "./pages/BeniciaSpanishPage";
import AnaheimSpanishPage from "./pages/AnaheimSpanishPage";
import SantaAnaSpanishPage from "./pages/SantaAnaSpanishPage";
import SpanishMoveOutPage from "./pages/SpanishMoveOutPage";
import SpanishDeepCleanPage from "./pages/SpanishDeepCleanPage";

import SpanishBlogPage from "./pages/SpanishBlogPage";
import SpanishBlogPostPage from "./pages/SpanishBlogPostPage";
import SpanishCityPage from "./pages/SpanishCityPage";
import SpanishLocationsPage from "./pages/SpanishLocationsPage";
import RegionBlogPageEs from "./pages/RegionBlogPageEs";
const SpanishPricingPage = lazy(() => import("./pages/SpanishPricingPage"));
const SpanishFAQPage = lazy(() => import("./pages/SpanishFAQPage"));
import RussianCityPage from "./pages/RussianCityPage";
import ChineseCityPage from "./pages/ChineseCityPage";
const RussianPricingPage = lazy(() => import("./pages/RussianPricingPage"));
const ChinesePricingPage = lazy(() => import("./pages/ChinesePricingPage"));
const RussianFAQPage = lazy(() => import("./pages/RussianFAQPage"));
const ChineseFAQPage = lazy(() => import("./pages/ChineseFAQPage"));
import RussianLocationsPage from "./pages/RussianLocationsPage";
import ChineseLocationsPage from "./pages/ChineseLocationsPage";
import RussianBlogPage from "./pages/RussianBlogPage";
import ChineseBlogPage from "./pages/ChineseBlogPage";
import RegionBlogPageRu from "./pages/RegionBlogPageRu";
import RegionBlogPageZh from "./pages/RegionBlogPageZh";
import RussianBlogPostPage from "./pages/RussianBlogPostPage";
import ChineseBlogPostPage from "./pages/ChineseBlogPostPage";

import RussianMoveOutPage from "./pages/RussianMoveOutPage";
import ChineseMoveOutPage from "./pages/ChineseMoveOutPage";
import RussianDeepCleanPage from "./pages/RussianDeepCleanPage";
import ChineseDeepCleanPage from "./pages/ChineseDeepCleanPage";
import RussianReferralPage from "./pages/RussianReferralPage";
import ChineseReferralPage from "./pages/ChineseReferralPage";
import { FloatingDesktopCTA } from "./components/FloatingDesktopCTA";
import { ScrollToTop } from "./components/ScrollToTop";

import { BackToTop } from "./components/BackToTop";
import { GeoLocationBanner } from "./components/GeoLocationBanner";
import { ExitIntentPopup } from "./components/ExitIntentPopup";
import { FirstTimeCustomerPopup } from "./components/FirstTimeCustomerPopup";
import { RuEmailCapturePopup } from "./components/RuEmailCapturePopup";
import { ZhEmailCapturePopup } from "./components/ZhEmailCapturePopup";
import { RuReferralPopup } from "./components/RuReferralPopup";
import { ZhReferralPopup } from "./components/ZhReferralPopup";
import { MobileDiscountPopup } from "./components/MobileDiscountPopup";
import { WaitlistPopup } from "./components/WaitlistPopup";
import { RecentBookingSocialProof } from "./components/RecentBookingSocialProof";
import { BookingWidget } from "./components/BookingWidget";
import { GiftCardWidget } from "./components/GiftCardWidget";
import { SavedBookingBanner } from "./components/SavedBookingBanner";
const WhyRecurringPage = lazy(() => import("./pages/WhyRecurringPage"));
const GiftCardsPage = lazy(() => import("./pages/GiftCardsPage"));
const UplevelStandardPage = lazy(() => import("./pages/UplevelStandardPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const CancellationPolicyPage = lazy(() => import("./pages/CancellationPolicyPage"));
const TrustSafetyHubPage = lazy(() => import("./pages/TrustSafetyHubPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const CleaningPoliciesPage = lazy(() => import("./pages/CleaningPoliciesPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const LoyaltyDashboardPage = lazy(() => import("./pages/LoyaltyDashboardPage"));
const ReferralHubPage = lazy(() => import("./pages/ReferralHubPage"));
const RedeemHistoryPage = lazy(() => import("./pages/RedeemHistoryPage"));
const GiftCardBalancePage = lazy(() => import("./pages/GiftCardBalancePage"));
const CleaningTipsPage = lazy(() => import("./pages/CleaningTipsPage"));
const OfficeCleaningPage = lazy(() => import("./pages/OfficeCleaningPage"));
const PostConstructionPage = lazy(() => import("./pages/PostConstructionPage"));
const MoveInOutPage = lazy(() => import("./pages/MoveInOutPage"));
const CostGuidePage = lazy(() => import("./pages/CostGuidePage"));
const WhyChooseUsPage = lazy(() => import("./pages/WhyChooseUsPage"));
const ChecklistGeneratorPage = lazy(() => import("./pages/ChecklistGeneratorPage"));
const LeaveReviewPage = lazy(() => import("./pages/LeaveReviewPage"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const QuizPage = lazy(() => import("./pages/QuizPage"));
const CityCostGuidePage = lazy(() => import("./pages/CityCostGuidePage"));
const CustomerStoriesPage = lazy(() => import("./pages/CustomerStoriesPage"));
const ResourcesPage = lazy(() => import("./pages/ResourcesPage"));
const OffersPage = lazy(() => import("./pages/OffersPage"));
const QuizLandingPage = lazy(() => import("./pages/QuizLandingPage"));
const OnboardingPortalPage = lazy(() => import("./pages/OnboardingPortalPage"));
const JobLocationPage = lazy(() => import("./pages/JobLocationPage"));
const SeasonalPromoPage = lazy(() => import("./pages/SeasonalPromoPage"));
const AccessibilityStatementPage = lazy(() => import("./pages/AccessibilityStatementPage"));
const BusinessInfoPage = lazy(() => import("./pages/BusinessInfoPage"));
import { MobileStickyCTA } from "./components/MobileStickyCTA";
import { ServiceInclusionsModal } from "./components/ServiceInclusionsModal";
import { GlobalBookingListener } from "./components/GlobalBookingListener";
import { WelcomeBackToast } from "./components/WelcomeBackToast";
import FloatingLeadCapture from "./components/FloatingLeadCapture";
const GmbLandingPage = lazy(() => import("./pages/GmbLandingPage"));
const NeighborhoodPage = lazy(() => import("./pages/NeighborhoodPage"));
const FreeGuidesPage = lazy(() => import("./pages/FreeGuidesPage"));
const GoogleAdsLandingPage = lazy(() => import("./pages/GoogleAdsLandingPage"));

const queryClient = new QueryClient();
const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <WelcomeBackToast />
      <BrowserRouter>
        <LocationProvider>
        <GlobalBookingListener />
        <ScrollToTop />
        <FloatingDesktopCTA />
        <MobileStickyCTA />

        <BackToTop />
        <GeoLocationBanner />
        <MobileDiscountPopup />
        <ExitIntentPopup />
        <FirstTimeCustomerPopup />
        <WaitlistPopup />
        <RecentBookingSocialProof />
        <RuEmailCapturePopup />
        <ZhEmailCapturePopup />
        <RuReferralPopup />
        <ZhReferralPopup />
        <BookingWidget />
        <GiftCardWidget />
        <SavedBookingBanner />
        <ServiceInclusionsModal />
        <FloatingLeadCapture />
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div></div>}>
        <Routes>
          {/* ── Sitemap Routes Removed (handled by Vite plugin) ─── */}

          {/* ── Spanish Landing Pages ───────────────────────── */}
          <Route path="/es/sacramento" element={<SacramentoSpanishPage />} />
          <Route path="/es/los-angeles" element={<LASpanishPage />} />
          <Route path="/es/roseville" element={<RosevilleSpanishPage />} />
          <Route path="/es/pasadena" element={<PasadenaSpanishPage />} />
          <Route path="/es/san-jose" element={<SanJoseSpanishPage />} />
          <Route path="/es/san-diego" element={<SanDiegoSpanishPage />} />
          <Route path="/es/san-francisco" element={<SanFranciscoSpanishPage />} />
          <Route path="/es/irvine" element={<IrvineSpanishPage />} />
          <Route path="/es/oakland" element={<OaklandSpanishPage />} />
          <Route path="/es/west-la" element={<WestLASpanishPage />} />
          <Route path="/es/santa-clara" element={<SantaClaraSpanishPage />} />
          <Route path="/es/marin" element={<MarinSpanishPage />} />
          <Route path="/es/contra-costa" element={<ContraCostaSpanishPage />} />
          <Route path="/es/solano" element={<SolanoSpanishPage />} />
          <Route path="/es/alameda" element={<AlamedaSpanishPage />} />
          <Route path="/es/fremont" element={<FremontSpanishPage />} />
          <Route path="/es/hayward" element={<HaywardSpanishPage />} />
          <Route path="/es/berkeley" element={<BerkeleySpanishPage />} />
          <Route path="/es/pleasanton" element={<PleasantonSpanishPage />} />
          <Route path="/es/dublin" element={<DublinSpanishPage />} />
          <Route path="/es/livermore" element={<LivermoreSpanishPage />} />
          <Route path="/es/tri-valley" element={<TriValleySpanishPage />} />
          <Route path="/es/san-ramon" element={<SanRamonSpanishPage />} />
          <Route path="/es/orange-county" element={<OrangeCountySpanishPage />} />
          <Route path="/es/huntington-beach" element={<HuntingtonBeachSpanishPage />} />
          <Route path="/es/newport-beach" element={<NewportBeachSpanishPage />} />
          <Route path="/es/vallejo" element={<VallejoSpanishPage />} />
          <Route path="/es/fairfield" element={<FairfieldSpanishPage />} />
          <Route path="/es/vacaville" element={<VacavilleSpanishPage />} />
          <Route path="/es/benicia" element={<BeniciaSpanishPage />} />
          <Route path="/es/anaheim" element={<AnaheimSpanishPage />} />
          <Route path="/es/santa-ana" element={<SantaAnaSpanishPage />} />
          <Route path="/es/locations" element={<SpanishLocationsPage />} />
          <Route path="/es/precios" element={<SpanishPricingPage />} />
          <Route path="/es/preguntas-frecuentes" element={<SpanishFAQPage />} />
          <Route path="/es/blog" element={<SpanishBlogPage />} />
          <Route path="/es/locations/:region/blog" element={<RegionBlogPageEs />} />
          <Route path="/es/blog/:slug" element={<SpanishBlogPostPage />} />
          {/* Spanish Move-Out Cleaning pages — BEFORE the generic catch-all */}
          <Route path="/es/limpieza-mudanza/:citySlug" element={<SpanishMoveOutPage />} />
          <Route path="/es/limpieza-mudanza" element={<SpanishMoveOutPage />} />
          {/* Spanish Deep-Clean pages */}
          <Route path="/es/limpieza-profunda/:citySlug" element={<SpanishDeepCleanPage />} />
          <Route path="/es/limpieza-profunda" element={<SpanishDeepCleanPage />} />
          {/* Generic Spanish city pages — handles all remaining /es/:citySlug routes */}
          <Route path="/es/:citySlug" element={<SpanishCityPage />} />

          {/* ── Russian Landing Pages ───────────────────────── */}
          <Route path="/ru/pricing" element={<RussianPricingPage />} />
          <Route path="/ru/faq" element={<RussianFAQPage />} />
          <Route path="/ru/locations" element={<RussianLocationsPage />} />
          <Route path="/ru/blog" element={<RussianBlogPage />} />
          <Route path="/ru/locations/:region/blog" element={<RegionBlogPageRu />} />
          <Route path="/ru/blog/:slug" element={<RussianBlogPostPage />} />

          <Route path="/ru/limpieza-mudanza/:citySlug" element={<RussianMoveOutPage />} />
          <Route path="/ru/limpieza-mudanza" element={<RussianMoveOutPage />} />
          <Route path="/ru/limpieza-profunda/:citySlug" element={<RussianDeepCleanPage />} />
          <Route path="/ru/limpieza-profunda" element={<RussianDeepCleanPage />} />
          <Route path="/ru/referral" element={<RussianReferralPage />} />
          <Route path="/ru/:citySlug" element={<RussianCityPage />} />

          {/* ── Chinese Landing Pages ───────────────────────── */}
          <Route path="/zh/pricing" element={<ChinesePricingPage />} />
          <Route path="/zh/faq" element={<ChineseFAQPage />} />
          <Route path="/zh/locations" element={<ChineseLocationsPage />} />
          <Route path="/zh/blog" element={<ChineseBlogPage />} />
          <Route path="/zh/locations/:region/blog" element={<RegionBlogPageZh />} />
          <Route path="/zh/blog/:slug" element={<ChineseBlogPostPage />} />

          <Route path="/zh/limpieza-mudanza/:citySlug" element={<ChineseMoveOutPage />} />
          <Route path="/zh/limpieza-mudanza" element={<ChineseMoveOutPage />} />
          <Route path="/zh/limpieza-profunda/:citySlug" element={<ChineseDeepCleanPage />} />
          <Route path="/zh/limpieza-profunda" element={<ChineseDeepCleanPage />} />
          <Route path="/zh/referral" element={<ChineseReferralPage />} />
          <Route path="/zh/:citySlug" element={<ChineseCityPage />} />

          {/* ── Core pages ─────────────────────────────────── */}
          <Route path="/" element={<Index />} />
          <Route path="/promo" element={<SeasonalPromoPage />} />
          <Route path="/services" element={<ServicesOverview />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/pricing-calculator" element={<PricingCalculatorPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/join-team" element={<JoinTeamPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/book" element={<BookingPage />} />
          <Route path="/why-recurring" element={<WhyRecurringPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/dashboard/loyalty" element={<LoyaltyDashboardPage />} />
          <Route path="/dashboard/referrals" element={<ReferralHubPage />} />
          <Route path="/dashboard/loyalty/history" element={<RedeemHistoryPage />} />
          <Route path="/gift-card-balance" element={<GiftCardBalancePage />} />
          <Route path="/cleaning-tips" element={<CleaningTipsPage />} />
          {/* ── Service type pages ─────────────────────────── */}
          <Route path="/services/office-cleaning" element={<OfficeCleaningPage />} />
          <Route path="/services/post-construction" element={<PostConstructionPage />} />
          <Route path="/services/move-in-out" element={<MoveInOutPage />} />
          <Route path="/services/deep-cleaning" element={<Navigate to="/services/deep-clean" replace />} />
          <Route path="/services/:type" element={<ServicePage />} />

          {/* ── Location pages ─────────────────────────────── */}
          <Route path="/locations" element={<ServiceAreaPage />} />
          <Route path="/locations/:region/blog" element={<RegionBlogPage />} />
          <Route path="/locations/:region/faq" element={<RegionFAQPage />} />
          <Route path="/locations/la/:subregion" element={<LASubRegionPage />} />
          <Route path="/locations/:city" element={<LocationPage />} />

          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="/cost-guide" element={<CostGuidePage />} />
          <Route path="/gift-cards" element={<GiftCardsPage />} />
          <Route path="/uplevel-standard" element={<UplevelStandardPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/cancellation-policy" element={<CancellationPolicyPage />} />
          <Route path="/trust-safety" element={<TrustSafetyHubPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/cleaning-policies" element={<CleaningPoliciesPage />} />
          <Route path="/why-choose-us" element={<WhyChooseUsPage />} />
          <Route path="/custom-checklist" element={<ChecklistGeneratorPage />} />
          <Route path="/leave-review" element={<LeaveReviewPage />} />
          <Route path="/quiz" element={<QuizPage />} />
          <Route path="/quiz-offer" element={<QuizLandingPage />} />
          <Route path="/house-cleaning-cost-:citySlug" element={<CityCostGuidePage />} />
          <Route path="/customer-stories" element={<CustomerStoriesPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/offers" element={<OffersPage />} />
          <Route path="/onboarding" element={<OnboardingPortalPage />} />
          <Route path="/cleaning-jobs-:region" element={<JobLocationPage />} />
          <Route path="/accessibility-statement" element={<AccessibilityStatementPage />} />
          <Route path="/business-info" element={<BusinessInfoPage />} />
          <Route path="/local/:city" element={<GmbLandingPage />} />
          <Route path="/neighborhood/:neighborhoodSlug" element={<NeighborhoodPage />} />
          <Route path="/free-guides" element={<FreeGuidesPage />} />
          <Route path="/lp/:citySlug" element={<GoogleAdsLandingPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </LocationProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
// 